import { useMismatchWarningRules } from '@/components/ReportForm/hooks/useMismatchWarningRules';
import { useModelsToOptionsWithData } from '@/hooks/useTypeToOptions';
import { useTranslation } from '@/i18n';
import {
  FormSelect,
  FormSelectProps,
  SelectProps,
} from '@moneyforward/ap-frontend-components';
import { Bank as BankModel, useGetBanks } from 'ap-openapi';
import {
  Dispatch,
  memo,
  SetStateAction,
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  FieldPath,
  FieldValues,
  Path,
  PathValue,
  RegisterOptions,
  useFormContext,
} from 'react-hook-form';

type Options = {
  label: string;
  value: string;
};

type Props<TFieldValues extends FieldValues> = {
  selectProps?: Omit<FormSelectProps<TFieldValues>['selectProps'], 'options'>;
  defaultOptions?: Options[];
  bankBranchName?: FieldPath<TFieldValues>;
  setDraftInstantPayeeBankName?: Dispatch<SetStateAction<string | undefined>>;
  mismatchMessage?: string;
} & Omit<FormSelectProps<TFieldValues>, 'selectProps' | 'label'>;

export const _BanksSelect = <TFieldValues extends FieldValues>({
  selectProps: _selectProps,
  defaultOptions: _defaultOptions,
  bankBranchName,
  setDraftInstantPayeeBankName,
  mismatchMessage,
  rules,
  name,
  ...rest
}: Props<TFieldValues>) => {
  const defaultOptions = useMemo(
    () => _defaultOptions ?? [],
    [_defaultOptions]
  );
  const { t } = useTranslation();
  const isRequired = Boolean(rules?.required);
  const requiredMessage =
    typeof rules?.required === 'string' ? rules.required : t('required');
  const { rules: mismatchRules, captionRender } = useMismatchWarningRules(
    name,
    isRequired,
    requiredMessage,
    mismatchMessage,
    undefined
  );
  const mergedRules = useMemo(
    () =>
      // Spreading RegisterOptions widens its valueAsDate / valueAsNumber
      // union, so the merged rules need an assertion. Built-in required
      // stays off; warningOrError handles it.
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      ({
        ...rules,
        required: undefined,
        ...mismatchRules,
      } as RegisterOptions<TFieldValues>),
    [rules, mismatchRules]
  );
  const [query, setQuery] = useState<string | undefined>(undefined);
  const { data: apiData, error, isLoading } = useGetBanks();
  if (error) throw error;
  const items = useMemo(() => apiData?.data.banks ?? [], [apiData?.data.banks]);
  const data = useMemo(
    () =>
      items.filter((item) =>
        query ? item.name?.toUpperCase().includes(query.toUpperCase()) : true
      ),
    [items, query]
  );
  const options = useModelsToOptionsWithData<BankModel>(
    data,
    query,
    defaultOptions,
    {
      labelKeys: ['name'],
      value: 'id',
    }
  );
  const onSearch = useCallback((value: string) => {
    startTransition(() => {
      setQuery(value);
    });
  }, []);
  const { onChangeAfter } = rest;
  const { setValue, watch, getValues, trigger, clearErrors } =
    useFormContext<TFieldValues>();

  const onChange = useCallback(
    (value: string | number | (string | number)[]) => {
      if (bankBranchName) {
        const hadBranch = !!getValues(bankBranchName);
        setValue(
          bankBranchName,
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          null as PathValue<TFieldValues, Path<TFieldValues>>
        );
        // The branch's OCR warning lives in the library's validation state
        // and only clears when its validation reruns. Run it now, before the
        // branch is disabled (disabled fields are never validated), then drop
        // the required error it raises so the untouched branch shows nothing.
        if (hadBranch) {
          void trigger(bankBranchName).then(() => clearErrors(bankBranchName));
        }
      }

      onChangeAfter?.(value);
    },
    [bankBranchName, onChangeAfter, setValue, getValues, trigger, clearErrors]
  );

  const watchedValue = watch(name);
  useEffect(() => {
    if (!watchedValue && setDraftInstantPayeeBankName) {
      setDraftInstantPayeeBankName(undefined);
      return;
    }

    if (watchedValue && options && setDraftInstantPayeeBankName) {
      const selectedOption = options.find(
        (option) => option.value === watchedValue
      );
      setDraftInstantPayeeBankName(selectedOption?.label);
    }
  }, [options, watchedValue, setDraftInstantPayeeBankName]);

  const selectProps: SelectProps = useMemo(
    () => ({
      comboBox: true,
      placeholder: t('bank_select_placeholder'),
      inputPlaceholder: t('bank_input_placeholder'),
      ..._selectProps,
      options: options,
      onSearch: onSearch,
      loading: isLoading,
      mode: 'single',
      selectAll: undefined,
      selectAllLabel: undefined,
    }),
    [_selectProps, isLoading, options, t, onSearch]
  );

  return (
    <FormSelect
      selectProps={selectProps}
      name={name}
      {...rest}
      rules={mergedRules}
      captionRender={captionRender}
      onChangeAfter={onChange}
    />
  );
};
_BanksSelect.displayName = '_BanksSelect';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const BanksSelect = memo((props) => (
  <_BanksSelect {...props} />
)) as typeof _BanksSelect;
BanksSelect.displayName = 'BanksSelect';
